import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import LiveVersusPanel from '@/components/coveredCall/LiveVersusPanel';

const FAQS = [
  {
    q: 'What is the main difference between JEPI and JEPQ?',
    a: 'JEPI tracks low-volatility S&P 500 names; JEPQ tracks Nasdaq-100 names. Both write covered calls to generate income. JEPQ runs hotter — higher yield, higher volatility, more upside in tech rallies. JEPI is calmer — lower yield, lower drawdowns, less correlation with tech.',
  },
  {
    q: 'Which has the higher dividend yield, JEPI or JEPQ?',
    a: 'Historically JEPQ has paid a higher trailing-twelve-month yield (roughly 9-11%) than JEPI (roughly 7-9%). The premium reflects the higher implied volatility of the Nasdaq-100 vs the S&P 500 — covered-call writers get paid more for selling vol when there is more vol to sell.',
  },
  {
    q: 'Are JEPI and JEPQ dividends qualified or ordinary?',
    a: 'A meaningful portion of both funds\' distributions are NOT qualified — the call-option income flows through as ordinary income, taxed at your full marginal rate. The equity-component dividends embedded in the holdings remain qualified. The exact qualified percentage is reported on each year\'s 1099-DIV. If you hold JEPI/JEPQ in a Roth or traditional IRA, the distinction is moot.',
  },
  {
    q: 'Can I own both JEPI and JEPQ?',
    a: 'Yes — many income-focused investors split allocation between them. JEPI gives you the S&P 500 income flow, JEPQ gives you the Nasdaq income flow. They are correlated but not identical, and during tech-led drawdowns you will be glad you owned JEPI; during tech rallies you will be glad you owned JEPQ.',
  },
  {
    q: 'Will JEPI / JEPQ keep paying double-digit yields forever?',
    a: 'No. Covered-call ETF yields are tied to implied volatility (the VIX for JEPI, the VXN for JEPQ). When markets are calm and vol collapses, option premiums shrink and distributions fall. Both funds explicitly state distribution amounts vary month-to-month. Plan for 5-8% as a conservative long-run base case.',
  },
];

export default function LearnJepiVsJepq() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="JEPI vs JEPQ — Covered Call ETF Comparison (2026) | DiviDrip"
      description="JEPI vs JEPQ side-by-side: yield, holdings, volatility, taxes. Which JPMorgan covered-call ETF fits your portfolio — and when to own both."
      canonical="https://twylightcrow.com/learn/jepi-vs-jepq"
      guidePath="/learn/jepi-vs-jepq"
      sectionLabel="Dividend Tiers & ETF Face-offs"
      sectionAnchor="tiers"
      kicker="Learn · ETF face-off"
      h1="JEPI vs JEPQ — Which Covered Call ETF Wins?"
      datePublished="2026-05-22"
      jsonLd={jsonLd}
      related={[
        { to: '/true-return/jepi-vs-jepq', label: 'True Return Chart — JEPI vs JEPQ, $10K after dividends and inflation' },
        { to: '/learn/covered-call-etfs', label: 'Covered call ETFs explained — JEPI, JEPQ, QYLD, XYLD' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers to check before any buy' },
        { to: '/learn/build-5k-monthly-income', label: 'How to build a $5K monthly income portfolio' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/screener', label: 'Stock Screener — filter by yield, sector, frequency' },
      ]}
    >
      <p>
        Two of the most-searched dividend ETFs of the last three years sit right next to
        each other on JPMorgan&apos;s shelf: <strong>JEPI</strong> (JPMorgan Equity Premium
        Income) and <strong>JEPQ</strong> (JPMorgan Nasdaq Equity Premium Income). They use
        the same strategy — own a basket of stocks, write monthly covered calls on top —
        but they own <em>different</em> baskets, and that one difference reshapes everything
        else.
      </p>

      <h2>Side-by-side, live</h2>
      <p>
        These figures come straight from our nightly data pipeline, not from a fact sheet —
        so the yields, distributions and total returns below are the current ones.
      </p>
      <LiveVersusPanel slug="jepi-vs-jepq" />

      <h2>The structural differences</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Metric</th>
            <th className="text-left font-semibold">JEPI</th>
            <th className="text-left font-semibold">JEPQ</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Underlying index</td>
            <td>Low-vol S&amp;P 500 subset</td>
            <td>Low-vol Nasdaq-100 subset</td>
          </tr>
          <tr>
            <td className="font-semibold">Holdings (approx)</td>
            <td>~130 names</td>
            <td>~80 names</td>
          </tr>
          <tr>
            <td className="font-semibold">Beta (vs S&amp;P 500)</td>
            <td>~0.6</td>
            <td>~0.8</td>
          </tr>
          <tr>
            <td className="font-semibold">Best-fit use case</td>
            <td>Defensive income</td>
            <td>Tech-tilted income</td>
          </tr>
        </tbody>
      </table>

      <h2>How the covered-call strategy works (60 seconds)</h2>
      <p>
        Both funds own a basket of stocks (≈ 130 names for JEPI; ≈ 80 for JEPQ). Each
        month the manager sells <em>out-of-the-money call options</em> on a portion of the
        portfolio (via equity-linked notes). The option premium is collected as cash and
        distributed to shareholders. If the market rips upward, some upside is capped by
        the sold calls; if the market drops or chops sideways, the premium income cushions
        the decline.
      </p>

      <h2>When to pick JEPI</h2>
      <ul>
        <li>You want monthly income with smaller drawdowns than the S&amp;P 500.</li>
        <li>You worry about a tech-led correction and don&apos;t want concentration in QQQ-style names.</li>
        <li>You&apos;re older, closer to retirement, or otherwise prioritising capital preservation.</li>
      </ul>

      <h2>When to pick JEPQ</h2>
      <ul>
        <li>You want the higher yield and accept the higher volatility.</li>
        <li>You have an existing position-overweight toward defensives and need tech-side income exposure.</li>
        <li>You&apos;re younger and can ride the bigger swings.</li>
      </ul>

      <h2>The case for owning both</h2>
      <p>
        Many DiviDrip users hold a 50/50 or 60/40 (JEPI/JEPQ) split. The pair&apos;s
        correlation is high but not 1.0; during the 2022 Nasdaq drawdown JEPI fell about
        half as much as JEPQ, and during the 2023 tech rally JEPQ outpaced JEPI by 10+
        percentage points. Owning both smooths the income curve.
      </p>

      <h2>Compare them live in the app</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search <strong>JEPI</strong>, click the row to
        open the Stock Modal, then tap the <em>Compare</em> button and pick JEPQ as slot B.
        The Compare modal overlays both yield histories on the same chart, marks the
        crossovers with diamonds, and flashes a skew warning if one fund&apos;s peak yield
        is more than 3× the other. The <em>Log</em> toggle is useful here because JEPQ&apos;s
        peak distribution months tower over JEPI&apos;s — linear scale can compress JEPI&apos;s
        line.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: This is not investment advice. Yields, holdings, and expense ratios can
        change; always check the fund&apos;s latest factsheet at JPMorgan and the live
        numbers in DiviDrip before buying.
      </p>
    </LearnPage>
  );
}
